/**
 * LifeEventIcons.ts
 * Handcrafted inline SVG iconography for 10 dynamic life events:
 * - 5 Obstacles (Rent Spike, Parking Ticket, Laptop Breakdown, Food Poisoning, Plumbing Leak)
 * - 5 Windfalls (Viral Tip, Tax Refund, Mentor Bonus, Lottery Scratcher, Found Wallet)
 */

export const LIFE_EVENT_SVG_ICONS: Record<string, string> = {
  // --- 5 Obstacles ---

  /** Rent Spike: Apartment building with rising red dollar bill / eviction notice */
  event_rent_spike: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Apartment Building -->
    <rect x="10" y="14" width="28" height="28" rx="2" fill="#1e293b" stroke="#ef4444" stroke-width="1.8"/>
    <!-- Windows -->
    <rect x="14" y="18" width="4" height="4" fill="#fbbf24"/>
    <rect x="22" y="18" width="4" height="4" fill="#64748b"/>
    <rect x="30" y="18" width="4" height="4" fill="#fbbf24"/>
    <rect x="14" y="26" width="4" height="4" fill="#64748b"/>
    <rect x="22" y="26" width="4" height="4" fill="#fbbf24"/>
    <rect x="30" y="26" width="4" height="4" fill="#64748b"/>
    <!-- Red Price Surge Badge -->
    <circle cx="34" cy="14" r="8" fill="#ef4444" stroke="#991b1b" stroke-width="1.2"/>
    <path d="M34 10 L 37 13 L 31 13 Z" fill="#ffffff"/>
    <text x="34" y="18" font-size="8" font-weight="900" text-anchor="middle" fill="#ffffff">$</text>
  </svg>`,

  /** Parking Ticket: Parking violation ticket with red alert stamp */
  event_parking_ticket: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Paper Ticket -->
    <rect x="12" y="8" width="24" height="32" rx="2" fill="#f8fafc" stroke="#cbd5e1" stroke-width="1.5" transform="rotate(-5 12 8)"/>
    <!-- Red Violation Header -->
    <rect x="13" y="12" width="22" height="6" fill="#ef4444"/>
    <text x="24" y="17" font-size="5" font-weight="bold" text-anchor="middle" fill="#ffffff">PARKING TICKET</text>
    <!-- Barcode / Lines -->
    <line x1="16" y1="22" x2="32" y2="22" stroke="#64748b" stroke-width="1.2"/>
    <line x1="16" y1="26" x2="28" y2="26" stroke="#64748b" stroke-width="1.2"/>
    <line x1="16" y1="30" x2="30" y2="30" stroke="#64748b" stroke-width="1.2"/>
    <!-- Red Penalty Stamp -->
    <rect x="18" y="32" width="16" height="5" rx="1" fill="none" stroke="#ef4444" stroke-width="1.2"/>
    <text x="26" y="36" font-size="4.5" font-weight="bold" text-anchor="middle" fill="#ef4444">VIOLATION</text>
  </svg>`,

  /** Laptop Breakdown: Smoking overheating laptop with warning triangle */
  event_laptop_breakdown: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Laptop Base -->
    <path d="M6 34 L 42 34 L 44 38 L 4 38 Z" fill="#334155" stroke="#64748b" stroke-width="1.2"/>
    <!-- Laptop Screen -->
    <rect x="10" y="14" width="28" height="20" rx="2" fill="#0f172a" stroke="#ef4444" stroke-width="1.5"/>
    <!-- Glitch Lines -->
    <line x1="14" y1="20" x2="34" y2="20" stroke="#f43f5e" stroke-width="1.2"/>
    <line x1="14" y1="24" x2="26" y2="24" stroke="#06b6d4" stroke-width="1.2"/>
    <line x1="18" y1="28" x2="34" y2="28" stroke="#eab308" stroke-width="1.2"/>
    <!-- Rising Heat Smoke -->
    <path d="M18 10 Q 16 6 18 2" stroke="rgba(239, 68, 68, 0.6)" stroke-width="1.5" stroke-linecap="round" fill="none"/>
    <path d="M28 11 Q 30 7 28 3" stroke="rgba(239, 68, 68, 0.6)" stroke-width="1.5" stroke-linecap="round" fill="none"/>
  </svg>`,

  /** Food Poisoning: Medicine bottle, capsules, and uneasy stomach swirl */
  event_food_poisoning: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Medicine Bottle -->
    <rect x="12" y="16" width="16" height="22" rx="3" fill="#0284c7" stroke="#38bdf8" stroke-width="1.2"/>
    <rect x="15" y="11" width="10" height="5" rx="1" fill="#f8fafc"/>
    <!-- Rx Cross -->
    <line x1="20" y1="24" x2="20" y2="30" stroke="#ffffff" stroke-width="2" stroke-linecap="round"/>
    <line x1="17" y1="27" x2="23" y2="27" stroke="#ffffff" stroke-width="2" stroke-linecap="round"/>
    <!-- Two-Tone Capsule Pill -->
    <rect x="30" y="24" width="8" height="14" rx="4" fill="#ef4444" transform="rotate(-30 30 24)"/>
    <rect x="30" y="31" width="8" height="7" rx="0" fill="#f8fafc" transform="rotate(-30 30 24)"/>
  </svg>`,

  /** Plumbing Leak: Burst pipe with dripping water and droplets */
  event_plumbing_leak: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Pipe Elbow -->
    <path d="M8 18 L 24 18 L 24 38 L 18 38 L 18 24 L 8 24 Z" fill="#64748b" stroke="#334155" stroke-width="1.5"/>
    <!-- Pipe Flange Rings -->
    <rect x="6" y="16" width="4" height="10" rx="1" fill="#94a3b8"/>
    <!-- Leak Crack & Spraying Water Jets -->
    <path d="M24 22 Q 34 16 38 20 Q 30 24 24 24 Z" fill="#38bdf8"/>
    <circle cx="38" cy="24" r="2.2" fill="#0284c7"/>
    <circle cx="34" cy="30" r="1.8" fill="#38bdf8"/>
    <circle cx="30" cy="36" r="2" fill="#0284c7"/>
    <!-- Puddle beneath -->
    <ellipse cx="24" cy="42" rx="14" ry="3.5" fill="rgba(56, 189, 248, 0.4)"/>
  </svg>`,

  // --- 5 Windfalls ---

  /** Viral Tip: Fan of green cash dollar bills with shining stars */
  event_viral_tip: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Cash Bill 1 (Back angled) -->
    <rect x="10" y="16" width="24" height="15" rx="2" fill="#059669" stroke="#34d399" stroke-width="1.2" transform="rotate(-15 10 16)"/>
    <!-- Cash Bill 2 (Front angled) -->
    <rect x="14" y="18" width="24" height="15" rx="2" fill="#10b981" stroke="#6ee7b7" stroke-width="1.2" transform="rotate(10 14 18)"/>
    <circle cx="27" cy="23" r="3.5" fill="#059669"/>
    <!-- Golden Sparkles / Stars -->
    <polygon points="12,10 13.5,13.5 17,14 13.5,15.5 12,19 10.5,15.5 7,14 10.5,13.5" fill="#facc15"/>
    <polygon points="38,12 39,14 41,15 39,16 38,18 37,16 35,15 37,14" fill="#facc15"/>
  </svg>`,

  /** Tax Refund: Official IRS mail envelope with refund cash check */
  event_tax_refund: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Envelope Body -->
    <rect x="8" y="14" width="32" height="24" rx="2" fill="#f8fafc" stroke="#cbd5e1" stroke-width="1.5"/>
    <path d="M8 14 L 24 28 L 40 14" stroke="#94a3b8" stroke-width="1.5"/>
    <!-- Refund Check popping out of envelope -->
    <rect x="12" y="8" width="24" height="14" rx="1" fill="#34d399" stroke="#059669" stroke-width="1"/>
    <circle cx="24" cy="14" r="2.5" fill="#047857"/>
    <text x="24" y="16" font-size="4" font-weight="bold" text-anchor="middle" fill="#ffffff">$</text>
  </svg>`,

  /** Mentor Bonus: Golden trophy cup with ribbon rosette */
  event_mentor_bonus: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Trophy Cup Base -->
    <rect x="18" y="38" width="12" height="4" rx="1" fill="#78350f"/>
    <rect x="22" y="32" width="4" height="6" fill="#f59e0b"/>
    <!-- Trophy Bowl -->
    <path d="M14 12 L 34 12 C 34 26, 14 26, 14 12 Z" fill="#fbbf24" stroke="#d97706" stroke-width="1.5"/>
    <!-- Trophy Handles -->
    <path d="M14 14 C 8 14, 8 22, 14 22" stroke="#d97706" stroke-width="2" stroke-linecap="round" fill="none"/>
    <path d="M34 14 C 40 14, 40 22, 34 22" stroke="#d97706" stroke-width="2" stroke-linecap="round" fill="none"/>
    <!-- Star on Bowl -->
    <polygon points="24,15 25,18 28,18 25.5,20 26.5,23 24,21 21.5,23 22.5,20 20,18 23,18" fill="#ffffff"/>
  </svg>`,

  /** Lottery Scratcher: Lucky 777 scratcher card with gold coins */
  event_lottery_scratcher: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Ticket Base -->
    <rect x="8" y="10" width="32" height="28" rx="3" fill="#6366f1" stroke="#4338ca" stroke-width="1.5"/>
    <!-- Scratcher Scratch Area -->
    <rect x="11" y="18" width="26" height="14" rx="2" fill="#e0e7ff"/>
    <!-- Lucky Triple 7s -->
    <text x="16" y="28" font-size="9" font-weight="900" fill="#dc2626">7</text>
    <text x="24" y="28" font-size="9" font-weight="900" fill="#dc2626">7</text>
    <text x="32" y="28" font-size="9" font-weight="900" fill="#dc2626">7</text>
  </svg>`,

  /** Found Wallet: Handshake of integrity and returned wallet */
  event_found_wallet: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Leather Wallet -->
    <rect x="10" y="12" width="28" height="24" rx="3" fill="#78350f" stroke="#451a03" stroke-width="1.8"/>
    <!-- Stitched Edge -->
    <rect x="12" y="14" width="24" height="20" rx="2" fill="#92400e"/>
    <!-- Wallet Flap & Clasp -->
    <path d="M26 20 L 38 20 C 38 24, 38 28, 26 28 Z" fill="#451a03"/>
    <circle cx="34" cy="24" r="1.8" fill="#fbbf24"/>
    <!-- Cash peaking out -->
    <rect x="14" y="8" width="20" height="6" rx="1" fill="#34d399"/>
  </svg>`,
};
